import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PmActivity, PmDrehbuchDiff, PmMicroScenarioView, PmOverview, PmTask, PmTaskAnnotation, PmVisit } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { pmBoardApi, pmDrehbuchApi, pmTaskApi, pmTaskMediaApi, pmVisitApi, uploadPmTaskMedia } from "../../api/client.js";

/**
 * Server state of the board (ADR-055 phase 5) — TanStack Query owns it, no store mirrors it. D9: there are no live
 * updates; a reload (and the refetch after every write) is the update mechanism.
 *
 * Every non-2xx answer becomes a {@link PmError} carrying the HTTP status and the contract's `code`, so a view can tell
 * "the board is not enabled here" (404 on the overview) from "the server is down".
 */
export class PmError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    what: string,
  ) {
    super(`${what} returned ${String(status)}`);
  }
}
const fail = (what: string, status: number, body: unknown): PmError => new PmError(status, (body as { code?: string } | null)?.code, what);

const keys = {
  all: (s: AuthSession | null) => ["pm", s?.realm, s?.sub] as const,
  overview: (s: AuthSession | null) => ["pm", s?.realm, s?.sub, "overview"] as const,
  ms: (s: AuthSession | null, id: number | null) => ["pm", s?.realm, s?.sub, "ms", id] as const,
  diff: (s: AuthSession | null, id: number | null) => ["pm", s?.realm, s?.sub, "diff", id] as const,
  taskNotes: (s: AuthSession | null, id: number | null) => ["pm", s?.realm, s?.sub, "task-notes", id] as const,
  taskActivity: (s: AuthSession | null, id: number | null) => ["pm", s?.realm, s?.sub, "task-activity", id] as const,
  msActivity: (s: AuthSession | null, id: number | null) => ["pm", s?.realm, s?.sub, "ms-activity", id] as const,
  /** Outside `all` on purpose: a board refresh must not swap every image's link and reload it. */
  mediaPreview: (s: AuthSession | null, id: number) => ["pm-media-preview", s?.realm, s?.sub, id] as const,
};

/** A 4xx is an answer (not enabled, not found, forbidden), never worth a retry. */
const retry = (count: number, error: Error) => !(error instanceof PmError && error.status < 500) && count < 1;

export function usePmOverview(session: AuthSession | null) {
  return useQuery<PmOverview, Error>({
    queryKey: keys.overview(session),
    enabled: session !== null,
    retry,
    queryFn: async () => {
      const res = await pmBoardApi.getPmOverview();
      if (res.status !== 200) throw fail("GET /pm", res.status, res.body);
      return res.body;
    },
  });
}

export function usePmMicroScenario(session: AuthSession | null, msId: number | null) {
  return useQuery<PmMicroScenarioView, Error>({
    queryKey: keys.ms(session, msId),
    enabled: session !== null && msId !== null,
    retry,
    queryFn: async () => {
      const res = await pmBoardApi.getPmMicroScenario({ params: { id: msId! }, query: {} });
      if (res.status !== 200) throw fail(`GET /pm/ms/${String(msId)}`, res.status, res.body);
      return res.body;
    },
  });
}

/** What changed since the previous Drehbuch version — fetched only when someone opens it. */
export function usePmDrehbuchDiff(session: AuthSession | null, msId: number | null, enabled: boolean) {
  return useQuery<PmDrehbuchDiff, Error>({
    queryKey: keys.diff(session, msId),
    enabled: session !== null && msId !== null && enabled,
    retry,
    queryFn: async () => {
      const res = await pmDrehbuchApi.getPmDrehbuchDiff({ params: { id: msId! }, query: {} });
      if (res.status !== 200) throw fail(`GET /pm/ms/${String(msId)}/drehbuch/diff`, res.status, res.body);
      return res.body;
    },
  });
}

export interface NewTaskInput {
  title: string;
  description: string;
  stepId: number | null;
  tagIds: number[];
  flagged: boolean;
  files: File[];
  /** Phase 6b: whether the dialog is still there to show a failure. When it is not, `onOrphaned` gets the files. */
  isOpen?: () => boolean;
  onOrphaned?: (failed: { fileNames: string[]; flagFailed: boolean }, taskTitle: string) => void;
}
/** What the create left undone: the task exists, but these follow-up steps failed (the dialog names them). */
export interface NewTaskOutcome {
  task: PmTask;
  failedFiles: Array<{ name: string; code: string | undefined }>;
  flagFailed: boolean;
}

/**
 * "Neuer Task": create the task, then — as separate calls, because the API has them separately — set the meeting flag
 * and upload each file. The task is the commitment; a failed upload or flag afterwards does NOT undo it, the outcome
 * reports it so the dialog can say so instead of pretending all went well or losing the task.
 */
export function useCreatePmTask(session: AuthSession | null, msId: number) {
  const qc = useQueryClient();
  return useMutation<NewTaskOutcome, Error, NewTaskInput>({
    mutationFn: async (input) => {
      const res = await pmTaskApi.createPmTask({
        params: { id: msId },
        body: { title: input.title, description: input.description, stepId: input.stepId, tagIds: input.tagIds },
      });
      if (res.status !== 201) throw fail(`POST /pm/ms/${String(msId)}/tasks`, res.status, res.body);
      const task = res.body;
      let flagFailed = false;
      if (input.flagged) {
        const f = await pmTaskApi.setPmTaskMeeting({ params: { id: task.id }, body: { flagged: true } }).catch(() => null);
        flagFailed = f?.status !== 200;
      }
      const failedFiles: NewTaskOutcome["failedFiles"] = [];
      for (const file of input.files) {
        try {
          await uploadPmTaskMedia(task.id, file);
        } catch (e) {
          failedFiles.push({ name: file.name, code: (e as { code?: string }).code });
        }
      }
      // Reported from HERE, not from a `mutate` callback: those die with the dialog (the phase-5 panel's open case).
      // Every follow-up that failed — the files AND the meeting flag (diff panel).
      if ((failedFiles.length > 0 || flagFailed) && input.isOpen && !input.isOpen()) {
        input.onOrphaned?.({ fileNames: failedFiles.map((f) => f.name), flagFailed }, task.title);
      }
      return { task, failedFiles, flagFailed };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.all(session) }),
  });
}

/** Variant annotations — the same rules as a step's. */
export function useVariantAnnotationWrites(session: AuthSession | null, msId: number) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.ms(session, msId) });
  const create = useMutation<unknown, Error, { variantId: number; text: string }>({
    mutationFn: async ({ variantId, text }) => {
      const res = await pmTaskApi.createPmVariantAnnotation({ params: { id: variantId }, body: { text } });
      if (res.status !== 201) throw fail(`POST /pm/variants/${String(variantId)}/annotations`, res.status, res.body);
      return res.body;
    },
    onSettled: refresh,
  });
  const update = useMutation<unknown, Error, { id: number; text: string }>({
    mutationFn: async ({ id, text }) => {
      const res = await pmTaskApi.updatePmVariantAnnotation({ params: { id }, body: { text } });
      if (res.status !== 200) throw fail(`PATCH /pm/variant-annotations/${String(id)}`, res.status, res.body);
      return res.body;
    },
    onSettled: refresh,
  });
  const remove = useMutation<unknown, Error, { id: number }>({
    mutationFn: async ({ id }) => {
      const res = await pmTaskApi.deletePmVariantAnnotation({ params: { id }, body: {} });
      if (res.status !== 200) throw fail(`DELETE /pm/variant-annotations/${String(id)}`, res.status, res.body);
      return res.body;
    },
    onSettled: refresh,
  });
  return { create, update, remove };
}

/** Step annotations (D2: every PM user writes; the author edits; the author or an admin deletes). */
export function useStepAnnotationWrites(session: AuthSession | null, msId: number) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.ms(session, msId) });
  const create = useMutation<unknown, Error, { stepId: number; text: string }>({
    mutationFn: async ({ stepId, text }) => {
      const res = await pmTaskApi.createPmStepAnnotation({ params: { id: stepId }, body: { text } });
      if (res.status !== 201) throw fail(`POST /pm/steps/${String(stepId)}/annotations`, res.status, res.body);
      return res.body;
    },
    onSettled: refresh,
  });
  const update = useMutation<unknown, Error, { id: number; text: string }>({
    mutationFn: async ({ id, text }) => {
      const res = await pmTaskApi.updatePmStepAnnotation({ params: { id }, body: { text } });
      if (res.status !== 200) throw fail(`PATCH /pm/step-annotations/${String(id)}`, res.status, res.body);
      return res.body;
    },
    onSettled: refresh,
  });
  const remove = useMutation<unknown, Error, { id: number }>({
    mutationFn: async ({ id }) => {
      const res = await pmTaskApi.deletePmStepAnnotation({ params: { id }, body: {} });
      if (res.status !== 200) throw fail(`DELETE /pm/step-annotations/${String(id)}`, res.status, res.body);
      return res.body;
    },
    onSettled: refresh,
  });
  return { create, update, remove };
}

// ── phase 6a: task commands, a task's notes + history, media ───────────────────────────────────────────────────────

/**
 * The task commands (D2: every PM user). Edit, move and delete carry the version the caller saw (D9): a 409
 * `PM_STALE` means someone else changed the task meanwhile — the caller shows that and the refetch below brings the new
 * state, so a second try works on fresh data. Every write refreshes the whole board (counts, columns, history).
 */
export function usePmTaskWrites(session: AuthSession | null) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.all(session) });
  const ok = <T,>(what: string, want: number, res: { status: number; body: unknown }): T => {
    if (res.status !== want) throw fail(what, res.status, res.body);
    return res.body as T;
  };
  return {
    move: useMutation<PmTask, Error, { id: number; version: number; columnId: number; beforeTaskId?: number | null | undefined }>({
      mutationFn: async ({ id, version, columnId, beforeTaskId }) =>
        ok(`PUT /pm/tasks/${String(id)}/column`, 200, await pmTaskApi.movePmTask({ params: { id }, body: { version, columnId, beforeTaskId } })),
      onSettled: refresh,
    }),
    update: useMutation<PmTask, Error, { id: number; version: number; title?: string; description?: string; stepId?: number | null }>({
      mutationFn: async ({ id, ...body }) => ok(`PATCH /pm/tasks/${String(id)}`, 200, await pmTaskApi.updatePmTask({ params: { id }, body })),
      onSettled: refresh,
    }),
    meeting: useMutation<PmTask, Error, { id: number; flagged: boolean }>({
      mutationFn: async ({ id, flagged }) =>
        ok(`PUT /pm/tasks/${String(id)}/meeting`, 200, await pmTaskApi.setPmTaskMeeting({ params: { id }, body: { flagged } })),
      onSettled: refresh,
    }),
    tags: useMutation<PmTask, Error, { id: number; version: number; tagIds: number[] }>({
      mutationFn: async ({ id, version, tagIds }) => ok(`PUT /pm/tasks/${String(id)}/tags`, 200, await pmTaskApi.setPmTaskTags({ params: { id }, body: { version, tagIds } })),
      onSettled: refresh,
    }),
    remove: useMutation<unknown, Error, { id: number; version: number }>({
      mutationFn: async ({ id, version }) =>
        ok(`DELETE /pm/tasks/${String(id)}`, 200, await pmTaskApi.deletePmTask({ params: { id }, query: { version }, body: {} })),
      onSettled: refresh,
    }),
    upload: useMutation<unknown, Error & { code?: string }, { taskId: number; file: File }>({
      mutationFn: ({ taskId, file }) => uploadPmTaskMedia(taskId, file),
      onSettled: refresh,
    }),
    removeMedia: useMutation<unknown, Error, { id: number }>({
      mutationFn: async ({ id }) =>
        ok(`DELETE /pm/task-media/${String(id)}`, 200, await pmTaskMediaApi.deletePmTaskMedia({ params: { id }, body: {} })),
      onSettled: refresh,
    }),
  };
}

/** A task's notes, oldest first (the details dialog). */
export function usePmTaskAnnotations(session: AuthSession | null, taskId: number | null) {
  return useQuery<PmTaskAnnotation[], Error>({
    queryKey: keys.taskNotes(session, taskId),
    enabled: session !== null && taskId !== null,
    retry,
    queryFn: async () => {
      const res = await pmTaskApi.listPmTaskAnnotations({ params: { id: taskId! } });
      if (res.status !== 200) throw fail(`GET /pm/tasks/${String(taskId)}/annotations`, res.status, res.body);
      return res.body.items;
    },
  });
}

/** A task's own history (the task, its notes, its media), newest first. */
export function usePmTaskActivity(session: AuthSession | null, taskId: number | null) {
  return useQuery<PmActivity[], Error>({
    queryKey: keys.taskActivity(session, taskId),
    enabled: session !== null && taskId !== null,
    retry,
    queryFn: async () => {
      const res = await pmTaskApi.getPmTaskActivity({ params: { id: taskId! } });
      if (res.status !== 200) throw fail(`GET /pm/tasks/${String(taskId)}/activity`, res.status, res.body);
      return res.body.items;
    },
  });
}

/** Task notes (D2: every PM user writes; the author edits; the author or a PM admin deletes). */
export function usePmTaskAnnotationWrites(session: AuthSession | null) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.all(session) });
  return {
    create: useMutation<unknown, Error, { taskId: number; text: string }>({
      mutationFn: async ({ taskId, text }) => {
        const res = await pmTaskApi.createPmTaskAnnotation({ params: { id: taskId }, body: { text } });
        if (res.status !== 201) throw fail(`POST /pm/tasks/${String(taskId)}/annotations`, res.status, res.body);
        return res.body;
      },
      onSettled: refresh,
    }),
    update: useMutation<unknown, Error, { id: number; text: string }>({
      mutationFn: async ({ id, text }) => {
        const res = await pmTaskApi.updatePmTaskAnnotation({ params: { id }, body: { text } });
        if (res.status !== 200) throw fail(`PATCH /pm/task-annotations/${String(id)}`, res.status, res.body);
        return res.body;
      },
      onSettled: refresh,
    }),
    remove: useMutation<unknown, Error, { id: number }>({
      mutationFn: async ({ id }) => {
        const res = await pmTaskApi.deletePmTaskAnnotation({ params: { id }, body: {} });
        if (res.status !== 200) throw fail(`DELETE /pm/task-annotations/${String(id)}`, res.status, res.body);
        return res.body;
      },
      onSettled: refresh,
    }),
  };
}

/** A short-lived presigned link to one media file (D7) — fetched per click, never cached (it expires). */
export async function fetchPmMediaUrl(id: number): Promise<string> {
  const res = await pmTaskMediaApi.getPmTaskMediaUrl({ params: { id }, query: {} });
  if (res.status !== 200) throw fail(`GET /pm/task-media/${String(id)}/url`, res.status, res.body);
  return res.body.url;
}

/** The link behind an image's preview, fresh per opened dialog (`gcTime: 0`): it expires, so it must not outlive the view. */
export function usePmMediaPreview(session: AuthSession | null, id: number, enabled: boolean) {
  return useQuery<string, Error>({
    queryKey: keys.mediaPreview(session, id),
    enabled: session !== null && enabled,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    queryFn: () => fetchPmMediaUrl(id),
  });
}

/** Whether an error is the version lock's 409 (D9): someone else changed the task first. */
export const isStale = (e: unknown): boolean => e instanceof PmError && e.code === "PM_STALE";

// ── phase 6b: the MS history and visits ───────────────────────────────────────────────────────────────────────────

/** The MS's history of its current phase, newest first (the Verlauf view and the "N neu" count). */
export function usePmMsActivity(session: AuthSession | null, msId: number | null) {
  return useQuery<PmActivity[], Error>({
    queryKey: keys.msActivity(session, msId),
    enabled: session !== null && msId !== null,
    retry,
    queryFn: async () => {
      const res = await pmBoardApi.getPmActivity({ params: { id: msId! }, query: {} });
      if (res.status !== 200) throw fail(`GET /pm/ms/${String(msId)}/activity`, res.status, res.body);
      return res.body.items;
    },
  });
}

/** Record "I have looked at this MS now"; resolves with the visit before it. */
export async function recordPmVisit(msId: number): Promise<PmVisit> {
  const res = await pmVisitApi.recordPmVisit({ params: { id: msId }, body: {} });
  if (res.status !== 200) throw fail(`PUT /pm/ms/${String(msId)}/visit`, res.status, res.body);
  return res.body;
}
